import type { TaskInstanceDto } from "@gw/shared-types";
import { fmtETDate } from "@/lib/format";
import { useKeyboardRowNav } from "@/lib/useKeyboardRowNav";
import { SortHead, type SortState } from "@/features/requestList/components/SortHead";

interface Props {
  rows: TaskInstanceDto[];
  sort: SortState;
  onSortChange: (s: SortState) => void;
  onRowClick: (requestId: string) => void;
  unit: "bd" | "cd";
}

type DerivedTaskStatus = "Completed" | "InProgress" | "NotStarted" | "Discarded";

/**
 * Derives the Task Status pill. Rules in priority order, matching the Request Detail Tasks table
 * and the backend filter (TaskInstance.DerivedStatus):
 *   1. wasDiscarded → Discarded.
 *   2. startedActionCount === 0 AND completedActionCount === 0 → Not started (genuinely untouched).
 *      An auto-completed task (no start, but a completion) falls through to Completed below.
 *   3. completedActionCount === actionCount AND a terminal-completion signal (taskCompletedDatetime
 *      non-null OR terminal raw status "Completed") → Completed. A task with all actions done but no
 *      completion signal (e.g. awaiting approval) stays In progress.
 *   4. Otherwise → In progress.
 */
function deriveTaskStatus(row: TaskInstanceDto): DerivedTaskStatus {
  if (row.wasDiscarded) return "Discarded";
  if (row.startedActionCount === 0 && row.completedActionCount === 0) return "NotStarted";
  const statusCompleted = row.status?.trim().toLowerCase() === "completed";
  return row.completedActionCount === row.actionCount &&
    (row.taskCompletedDatetime != null || statusCompleted)
    ? "Completed"
    : "InProgress";
}

const STATUS_LABEL: Record<DerivedTaskStatus, string> = {
  Completed: "Completed",
  InProgress: "In progress",
  NotStarted: "Not started",
  Discarded: "Discarded",
};

export function TaskInstancesTable({ rows, sort, onSortChange, onRowClick, unit }: Props) {
  const { getRowProps } = useKeyboardRowNav(rows.length, (i) => {
    const row = rows[i];
    if (row) onRowClick(row.requestId);
  });

  if (rows.length === 0) {
    return (
      <div className="empty">
        <h4>No matching task instances</h4>
        <p>Try expanding the date range or clearing some filters.</p>
      </div>
    );
  }

  return (
    <div className="tbl-wrap" style={{ maxHeight: "calc(100vh - 380px)" }}>
      <table className="tbl">
        <thead>
          <tr>
            <th>Task status</th>
            <SortHead label="Task" sortKey="taskName" sort={sort} setSort={onSortChange} />
            <th>Category</th>
            <th>Worker</th>
            <th>Client</th>
            <th>LOB</th>
            <th>Eng.</th>
            <th>Assigned to</th>
            <SortHead label="Started" sortKey="started" sort={sort} setSort={onSortChange} />
            <SortHead label="Duration" sortKey="duration" sort={sort} setSort={onSortChange} numeric />
            <SortHead label="Actions" sortKey="actions" sort={sort} setSort={onSortChange} numeric />
            <th>Reopened</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((t, i) => (
            <tr
              key={`${t.requestId}-${t.taskId}`}
              onClick={() => onRowClick(t.requestId)}
              {...getRowProps(i)}
            >
              <td>
                {(() => {
                  const derived = deriveTaskStatus(t);
                  return (
                    <span className={"status-pill " + derived.toLowerCase()}>
                      <span className="dot" />
                      {STATUS_LABEL[derived]}
                    </span>
                  );
                })()}
              </td>
              <td>
                <span className="cell-primary">{t.taskName}</span>
              </td>
              <td>
                <span className="cell-secondary">{t.taskCategory}</span>
              </td>
              <td>{t.workerName}</td>
              <td>{t.client}</td>
              <td>
                <span className="cell-secondary">{t.lineOfBusiness}</span>
              </td>
              <td>
                <span className="cell-secondary">{t.engagementType}</span>
              </td>
              <td>
                <span className="cell-secondary">{t.assignedToUserName}</span>
              </td>
              <td>
                {deriveTaskStatus(t) === "NotStarted" ? (
                  <span style={{ color: "var(--fg-3)" }}>—</span>
                ) : (
                  <span className="cell-secondary">{fmtETDate(new Date(t.startDatetime))}</span>
                )}
              </td>
              <td className="numeric">
                {(() => {
                  const derived = deriveTaskStatus(t);
                  if (derived === "Discarded" || derived === "NotStarted") {
                    return <span style={{ color: "var(--fg-3)" }}>—</span>;
                  }
                  return (
                    <div className="dur-stack">
                      {unit === "cd" ? (
                        <>
                          <span className="dur-bd">{t.durationCd.toFixed(2)} cd</span>
                          <span className="dur-cd">{t.durationBd.toFixed(2)} bd</span>
                        </>
                      ) : (
                        <>
                          <span className="dur-bd">{t.durationBd.toFixed(2)} bd</span>
                          <span className="dur-cd">{t.durationCd.toFixed(2)} cd</span>
                        </>
                      )}
                    </div>
                  );
                })()}
              </td>
              <td className="numeric">{t.actionCount}</td>
              <td>
                {t.wasReopened ? (
                  <span className="rework-icon">
                    <span className="material-symbols-rounded" style={{ fontSize: 14 }}>
                      refresh
                    </span>{" "}
                    yes
                  </span>
                ) : (
                  <span style={{ color: "var(--fg-3)" }}>—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
